// packages/shared/src/constants/pinbar.constants.ts
// Single source of truth for pinbar domain value-sets (ADR-20, section 5).
// All consumers (back schemas, drizzle tables, aggregates, flows, adapters,
// shared presets, front pages) must reference these exports — no inline literals.

import type { ChipVariant } from "../types/smart-table";

// ── pinbarType ──────────────────────────────────────────────────────────────
export const PINBAR_TYPE = ["bullish", "bearish", "none"] as const;
export type TPinbarType = (typeof PINBAR_TYPE)[number];
export const PINBAR_TYPE_OBJ = {
  BULLISH: "bullish",
  BEARISH: "bearish",
  NONE: "none",
} as const satisfies Record<string, TPinbarType>;
export const PINBAR_SORT_COLUMN = [
  "symbol",
  "type",
  "date",
  "pinbarStatus",
  "approveStatus",
  "strategyVersion",
  "timestamp.createdAt",
] as const;
export type TPinbarSortColumn = (typeof PINBAR_SORT_COLUMN)[number];
export const PINBAR_SORT_COLUMN_OBJ = {
  SYMBOL: "symbol",
  TYPE: "type",
  DATE: "date",
  PINBAR_STATUS: "pinbarStatus",
  APPROVE_STATUS: "approveStatus",
  STRATEGY_VERSION: "strategyVersion",
  CREATED_AT: "timestamp.createdAt",
} as const satisfies Record<string, TPinbarSortColumn>;
export const PINBAR_TYPE_LABELS: Record<TPinbarType, Record<string, string>> = {
  bullish: { "en-US": "Bullish", "ru-RU": "Бычий" },
  bearish: { "en-US": "Bearish", "ru-RU": "Медвежий" },
  none: { "en-US": "None", "ru-RU": "Нет" },
};
export const PINBAR_TYPE_VARIANTS: Record<TPinbarType, ChipVariant> = {
  bullish: "emerald",
  bearish: "destructive",
  none: "outline",
};

export const PINBAR_IDENTIFY_RESULT_STATUS_OBJ = {
  NO_PINBARS_FOUND: "no_pinbars_found",
} as const;

// Subset of pinbarType excluding "none" — used for quick-identification results.
export const PINBAR_TYPE_INITIAL = [
  PINBAR_TYPE_OBJ.BULLISH,
  PINBAR_TYPE_OBJ.BEARISH,
] as const;
export type TPinbarTypeInitial = (typeof PINBAR_TYPE_INITIAL)[number];

export const CANDLE_MODE = ["single", "multi"] as const;
export type TCandleMode = (typeof CANDLE_MODE)[number];
export const CANDLE_MODE_OBJ = {
  SINGLE: "single",
  MULTI: "multi",
} as const satisfies Record<string, TCandleMode>;

export const CANDLE_TIMEFRAME = ["daily", "weekly", "monthly"] as const;
export type TCandleTimeframe = (typeof CANDLE_TIMEFRAME)[number];
export const CANDLE_TIMEFRAME_OBJ = {
  DAILY: "daily",
  WEEKLY: "weekly",
  MONTHLY: "monthly",
} as const satisfies Record<string, TCandleTimeframe>;

// ── approveStatus ───────────────────────────────────────────────────────────
// Full schema set (7 values). Workflow: new → AI → to_shortlist → approved → job_done.
export const APPROVE_STATUS = [
  "new",
  "approvedByAi",
  "rejectedByAi",
  "to_shortlist",
  "approved",
  "job_done",
  "rejected",
] as const;
export type TApproveStatus = (typeof APPROVE_STATUS)[number];
export const APPROVE_STATUS_OBJ = {
  NEW: "new",
  APPROVED_BY_AI: "approvedByAi",
  REJECTED_BY_AI: "rejectedByAi",
  TO_SHORTLIST: "to_shortlist",
  APPROVED: "approved",
  JOB_DONE: "job_done",
  REJECTED: "rejected",
} as const satisfies Record<string, TApproveStatus>;

// Human-editable subset (5 values) — values a user can set in dropdown/preset.
// Excludes AI-only transitions (approvedByAi, rejectedByAi) which are system-set.
export const APPROVE_STATUS_HUMAN = [
  "new",
  "to_shortlist",
  "approved",
  "job_done",
  "rejected",
] as const satisfies readonly TApproveStatus[];
export type TApproveStatusHuman = (typeof APPROVE_STATUS_HUMAN)[number];

export const APPROVE_STATUS_LABELS: Record<
  TApproveStatus,
  Record<string, string>
> = {
  new: { "en-US": "New", "ru-RU": "Новый" },
  approvedByAi: { "en-US": "Approved by AI", "ru-RU": "Одобрено ИИ" },
  rejectedByAi: { "en-US": "Rejected by AI", "ru-RU": "Отклонено ИИ" },
  to_shortlist: { "en-US": "To shortlist", "ru-RU": "В шорт-лист" },
  approved: { "en-US": "Approved", "ru-RU": "Одобрен" },
  job_done: { "en-US": "Work finished", "ru-RU": "Работа окончена" },
  rejected: { "en-US": "Rejected", "ru-RU": "Отклонен" },
};

// Chip variants for rendering (all 7 values covered).
export const APPROVE_STATUS_VARIANTS: Record<TApproveStatus, ChipVariant> = {
  new: "outline",
  approvedByAi: "default",
  rejectedByAi: "destructive",
  to_shortlist: "default",
  approved: "default",
  job_done: "secondary",
  rejected: "destructive",
};

// ── pinbarStatus ────────────────────────────────────────────────────────────
export const PINBAR_STATUS = ["unknown", "success", "failure"] as const;
export type TPinbarStatus = (typeof PINBAR_STATUS)[number];
export const PINBAR_STATUS_OBJ = {
  UNKNOWN: "unknown",
  SUCCESS: "success",
  FAILURE: "failure",
} as const satisfies Record<string, TPinbarStatus>;
export const PINBAR_STATUS_LABELS: Record<
  TPinbarStatus,
  Record<string, string>
> = {
  unknown: { "en-US": "Unknown", "ru-RU": "Неизвестно" },
  success: { "en-US": "Success", "ru-RU": "ПИНБАР: Успех" },
  failure: { "en-US": "Failure", "ru-RU": "ПИНБАР: Провал" },
};
export const PINBAR_STATUS_VARIANTS: Record<TPinbarStatus, ChipVariant> = {
  unknown: "outline",
  success: "default",
  failure: "destructive",
};

// ── aiVerdict (YES/NO) ──────────────────────────────────────────────────────
// AI verdict for pinbar confirmation workflow (uppercase, AI output format).
export const AI_VERDICT = ["YES", "NO"] as const;
export type TAiVerdict = (typeof AI_VERDICT)[number];
export const AI_VERDICT_OBJ = {
  YES: "YES",
  NO: "NO",
} as const satisfies Record<string, TAiVerdict>;
